// ============================================================================
// project-summary-job-fields.ts: the revision state of the offline project summary
// ----------------------------------------------------------------------------
// The online project summary (GET /api/projects?summary=true) carries seven Job
// columns since migration 120: the project's own quote_job_id, quote_rev,
// source_quote_ref and source_issuer, and three joined from quote_jobs
// (title, quote_request_no, strata_plan). When that read fails as unreachable,
// the DataService proxy serves the Dexie summary instead, and the Run Sheet and
// All Quotes render it as if nothing happened. Without these columns a Rev 1
// quote reads as the Rev 0 that went to the client, an imported quote loses its
// number, and Job grouping and Job search stop working.
//
// A Revise runs on the server only (backend/src/routes/quote-jobs.js). In ONE
// UPDATE it moves quote_rev to N+1 and resets the send state: pdf_generated,
// email_sent and their times, quote_status 'pending', status 'sent' ->
// 'quoted'. This device's cached row is not written by it. So the revision and
// its send state travel together: showing the new Rev with the cached Rev 0
// send state would claim Rev 1 went to the client when only Rev 0 did.
//
// Sources:
//   - The last online summary, kept per quote in app_settings by the proxy
//     (only quotes that have Job data): the seven Job fields and the send
//     state, as the server reported them at that row's updatedAt.
//   - The cached project row. Its Job columns and send state are only as new as
//     the last WHOLE server row mirrored into it (a save, a duplicate, the
//     batch hydration), which stamps CACHED_SERVER_ROW_AT_FIELD with that row's
//     server updatedAt. The row's own updatedAt is NOT that version: the narrow
//     receipt writers (PDF, print, email, costings, outcome, follow-up, quote
//     status, Stage 5 completion) move it forward without carrying the Job
//     columns, other local writers stamp it with the browser clock, and a save
//     keeps the browser's snake updated_at beside the server's camel one.
//   - The narrow receipts this device mirrored after that whole row. Each one
//     records, per send-state group it wrote, the server time of its write in
//     LIFECYCLE_RECEIPTS_AT_FIELD.
//
// Resolution:
//   - The snapshot is current when the cached row has no server-row stamp (it
//     was cached by an older build, or by a path that does not know the server
//     row) or the snapshot's updatedAt is at or after the stamp. Then the Job
//     columns and the send state come from the snapshot, except each send-state
//     group this device wrote AFTER the snapshot, which keeps the cached value.
//   - Otherwise the cached row (the newer whole server row, plus any receipts
//     since) supplies all of it, as the offline summary always did.
//   - The join fields come from the snapshot whenever it describes the same Job.
//
// Failure direction: a whole-row writer that forgets to stamp makes the last
// online list win, which is at worst as old as that list. A narrow writer can
// no longer make the pre-Revise columns win.
//
// No code clears a project's Job today (quote_job_id is only ever set, and
// quote_jobs rows are never deleted), so a quote with no Job data in the online
// summary is not recorded, which keeps the snapshot to the Jobs quotes.
//
// Pure functions. The Dexie reads and writes live in data-service.js.
// ============================================================================

/**
 * app_settings key for the per-quote snapshot of the last online summary.
 * v2: each entry also carries the send state (v1 never shipped).
 */
export const PROJECT_SUMMARY_JOB_FIELDS_SETTING = 'projects.summaryJobFields.v2';

/** Cached-row field: server updatedAt of the last whole server row mirrored into it. */
export const CACHED_SERVER_ROW_AT_FIELD = 'cachedServerRowUpdatedAt';

/** Cached-row field: server time of each send-state group a narrow receipt wrote. */
export const LIFECYCLE_RECEIPTS_AT_FIELD = 'lifecycleReceiptsAt';

export interface SummaryJobFields {
    quoteJobId: string | null;
    quoteRev: number;
    sourceQuoteRef: string | null;
    sourceIssuer: string | null;
    quoteJobTitle: string | null;
    quoteJobQuoteRequestNo: string | null;
    quoteJobStrataPlan: string | null;
    /**
     * Jobs UX (JOBS-UX-SPEC 1.9): the kind of the quote. The server sends
     * split_area and imported_rev on both project lists. A cached project row
     * has neither, so they are derived from its ai_context: the split area when
     * projectSplit names this quote as the target, and the imported Rev from
     * importedQuote (its quoteRev, else the row's own Rev, so an older import
     * reads IMPORTED and never claims a revision it cannot prove).
     */
    splitArea: string | null;
    importedRev: number | null;
    /** Server only: the open site visit request of this Rev. Null offline without a snapshot. */
    siteVisitRequestedAt: string | null;
}

/** The send state a Revise resets, as the offline summary reports it. */
export interface SummaryLifecycleFields {
    status: string | null;
    quoteStatus: string | null;
    pdfGenerated: boolean;
    pdfGeneratedAt: string | null;
    emailSent: boolean;
    emailSentAt: string | null;
}

export interface SummaryJobFieldsSnapshotEntry extends SummaryJobFields {
    /** The summary row's updatedAt when it was recorded. */
    updatedAt: string | null;
    /** The send state the summary reported with these Job fields. */
    lifecycle: SummaryLifecycleFields;
}

export type SummaryJobFieldsSnapshot = Record<string, SummaryJobFieldsSnapshotEntry>;

/**
 * The send-state groups a narrow receipt can write, and the offline summary
 * fields each one owns.
 */
export const LIFECYCLE_RECEIPT_GROUPS = {
    pdf: ['pdfGenerated', 'pdfGeneratedAt'],
    email: ['emailSent', 'emailSentAt'],
    status: ['status'],
    quoteStatus: ['quoteStatus', 'quote_status'],
} as const;

export type LifecycleReceiptGroup = keyof typeof LIFECYCLE_RECEIPT_GROUPS;

/** The offline summary fields the resolution may override for one quote. */
export interface OfflineSummaryLifecycleOverride {
    status?: string | null;
    quoteStatus?: string | null;
    quote_status?: string | null;
    pdfGenerated?: boolean;
    pdfGeneratedAt?: string | null;
    emailSent?: boolean;
    emailSentAt?: string | null;
}

export interface OfflineSummaryRevisionState {
    jobFields: SummaryJobFields;
    /** Empty unless the last online summary is the current source. */
    lifecycle: OfflineSummaryLifecycleOverride;
}

const JOIN_FIELDS = ['quoteJobTitle', 'quoteJobQuoteRequestNo', 'quoteJobStrataPlan'] as const;

function text(value: unknown): string | null {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value !== 'string') return null;
    return value.trim() ? value : null;
}

function rev(value: unknown): number {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 && n <= 99 ? n : 0;
}

function pick(row: any, camel: string, snake: string): unknown {
    return row?.[camel] ?? row?.[snake];
}

function isoOrNull(value: unknown): string | null {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
    return typeof value === 'string' && value.trim() ? value : null;
}

function hasKey(row: any, camel: string, snake: string): boolean {
    return !!row && typeof row === 'object' && (camel in row || snake in row);
}

function aiContextOf(row: any): any {
    const ai = row?.aiContext ?? row?.ai_context;
    return ai && typeof ai === 'object' && !Array.isArray(ai) ? ai : null;
}

function splitAreaOf(row: any): string | null {
    if (hasKey(row, 'splitArea', 'split_area')) return text(pick(row, 'splitArea', 'split_area'));
    const split = aiContextOf(row)?.projectSplit;
    if (!split || typeof split !== 'object') return null;
    const id = text(row?.id);
    return id && text(split.targetProjectId) === id ? text(split.areaName)?.trim() || null : null;
}

function importedRevOf(row: any): number | null {
    if (hasKey(row, 'importedRev', 'imported_rev')) {
        const raw = pick(row, 'importedRev', 'imported_rev');
        if (raw === null || raw === undefined || raw === '') return null;
        const n = Number(raw);
        return Number.isInteger(n) && n >= 0 ? n : null;
    }
    const imported = aiContextOf(row)?.importedQuote;
    if (!imported || typeof imported !== 'object' || Array.isArray(imported)) return null;
    const n = Number(imported.quoteRev);
    if (imported.quoteRev !== null && imported.quoteRev !== undefined && Number.isInteger(n) && n >= 0) return n;
    return rev(pick(row, 'quoteRev', 'quote_rev'));
}

/** The Job fields of a project or summary row, read from either casing. */
export function readSummaryJobFields(row: any): SummaryJobFields {
    return {
        quoteJobId: text(pick(row, 'quoteJobId', 'quote_job_id')),
        quoteRev: rev(pick(row, 'quoteRev', 'quote_rev')),
        sourceQuoteRef: text(pick(row, 'sourceQuoteRef', 'source_quote_ref')),
        sourceIssuer: text(pick(row, 'sourceIssuer', 'source_issuer')),
        quoteJobTitle: text(pick(row, 'quoteJobTitle', 'quote_job_title')),
        quoteJobQuoteRequestNo: text(pick(row, 'quoteJobQuoteRequestNo', 'quote_job_quote_request_no')),
        quoteJobStrataPlan: text(pick(row, 'quoteJobStrataPlan', 'quote_job_strata_plan')),
        splitArea: splitAreaOf(row),
        importedRev: importedRevOf(row),
        siteVisitRequestedAt: isoOrNull(pick(row, 'siteVisitRequestedAt', 'site_visit_requested_at')),
    };
}

/** The send state of a project or summary row, read from either casing. */
export function readSummaryLifecycleFields(row: any): SummaryLifecycleFields {
    return {
        status: text(row?.status),
        quoteStatus: text(pick(row, 'quoteStatus', 'quote_status')),
        pdfGenerated: Boolean(pick(row, 'pdfGenerated', 'pdf_generated')),
        pdfGeneratedAt: isoOrNull(pick(row, 'pdfGeneratedAt', 'pdf_generated_at')),
        emailSent: Boolean(pick(row, 'emailSent', 'email_sent')),
        emailSentAt: isoOrNull(pick(row, 'emailSentAt', 'email_sent_at')),
    };
}

function hasJobData(fields: SummaryJobFields): boolean {
    return Boolean(fields.quoteJobId || fields.quoteRev > 0 || fields.sourceQuoteRef || fields.sourceIssuer);
}

function timeOf(value: unknown): number {
    if (typeof value !== 'string' && !(value instanceof Date)) return Number.NaN;
    return new Date(value as string).getTime();
}

/** A server row's version: its camel updatedAt first (apiFetch canon), else the snake one. */
function serverRowVersion(serverRow: any): string | null {
    return isoOrNull(serverRow?.updatedAt) ?? isoOrNull(serverRow?.updated_at);
}

/**
 * The cached row a whole server row becomes, stamped with that server row's
 * version. Call it only where the row being cached carries the server's whole
 * row (every project column), never for a narrow receipt. When the server row
 * has no readable version the stamp is removed, which makes the last online
 * list the current source rather than a stale stamp.
 */
export function stampCachedServerRow<T extends Record<string, any>>(cachedRow: T, serverRow: unknown): T {
    if (!cachedRow || typeof cachedRow !== 'object') return cachedRow;
    const version = serverRowVersion(serverRow);
    if (version) return { ...cachedRow, [CACHED_SERVER_ROW_AT_FIELD]: version };
    if (!(CACHED_SERVER_ROW_AT_FIELD in cachedRow)) return cachedRow;
    const { [CACHED_SERVER_ROW_AT_FIELD]: _stale, ...rest } = cachedRow;
    return rest as T;
}

/**
 * The cached row after a narrow receipt wrote the given send-state groups at
 * `at` (the server's updatedAt from the receipt, or the local write time when
 * the server sent none). Groups it did not write keep their earlier times.
 */
export function withLifecycleReceipts<T extends Record<string, any>>(
    cachedRow: T,
    groups: readonly LifecycleReceiptGroup[],
    at: unknown,
): T {
    const when = isoOrNull(at);
    if (!cachedRow || typeof cachedRow !== 'object' || !when || groups.length === 0) return cachedRow;
    const prior = cachedRow[LIFECYCLE_RECEIPTS_AT_FIELD];
    const receipts: Record<string, string> = prior && typeof prior === 'object' && !Array.isArray(prior)
        ? { ...prior }
        : {};
    for (const group of groups) receipts[group] = when;
    return { ...cachedRow, [LIFECYCLE_RECEIPTS_AT_FIELD]: receipts };
}

/**
 * The per-quote snapshot of an online summary: every row with Job data, keyed
 * by project id in id order (the summary arrives newest first, so its order
 * moves on every save; sorting keeps an unchanged snapshot byte-identical and
 * spares the write). Rows without an id or without Job data are left out.
 */
export function buildSummaryJobFieldsSnapshot(rows: unknown): SummaryJobFieldsSnapshot {
    const entries: Array<[string, SummaryJobFieldsSnapshotEntry]> = [];
    if (!Array.isArray(rows)) return {};
    for (const row of rows) {
        const id = text((row as any)?.id);
        if (!id) continue;
        const fields = readSummaryJobFields(row);
        if (!hasJobData(fields)) continue;
        entries.push([id, {
            ...fields,
            updatedAt: serverRowVersion(row),
            lifecycle: readSummaryLifecycleFields(row),
        }]);
    }
    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return Object.fromEntries(entries);
}

function lifecycleOverride(
    entry: SummaryLifecycleFields,
    skip: ReadonlySet<LifecycleReceiptGroup>,
): OfflineSummaryLifecycleOverride {
    const out: OfflineSummaryLifecycleOverride = {};
    if (!skip.has('status')) out.status = entry.status;
    if (!skip.has('quoteStatus')) {
        out.quoteStatus = entry.quoteStatus;
        out.quote_status = entry.quoteStatus;
    }
    if (!skip.has('pdf')) {
        out.pdfGenerated = entry.pdfGenerated;
        out.pdfGeneratedAt = entry.pdfGeneratedAt;
    }
    if (!skip.has('email')) {
        out.emailSent = entry.emailSent;
        out.emailSentAt = entry.emailSentAt;
    }
    return out;
}

/**
 * The revision state the offline summary reports for one cached project row:
 * its seven Job fields, and the send-state fields that override the cached
 * row's own (an empty override keeps them).
 */
export function resolveOfflineSummaryRevisionState(
    row: any,
    entry?: SummaryJobFieldsSnapshotEntry | null,
): OfflineSummaryRevisionState {
    const own = readSummaryJobFields(row);
    const usable = entry && typeof entry === 'object' && entry.lifecycle && typeof entry.lifecycle === 'object';
    if (!usable) return { jobFields: own, lifecycle: {} };

    const snap = readSummaryJobFields(entry);
    const cachedAt = timeOf(row?.[CACHED_SERVER_ROW_AT_FIELD]);
    const snapAt = timeOf(entry.updatedAt);
    const snapshotIsCurrent = !Number.isFinite(cachedAt) || (Number.isFinite(snapAt) && snapAt >= cachedAt);

    const base = snapshotIsCurrent ? snap : own;
    const job = base.quoteJobId;
    const joined = (key: typeof JOIN_FIELDS[number]): string | null => {
        if (!job) return null;
        if (snap.quoteJobId === job && snap[key]) return snap[key];
        if (own.quoteJobId === job && own[key]) return own[key];
        return null;
    };
    const jobFields: SummaryJobFields = {
        quoteJobId: base.quoteJobId,
        quoteRev: base.quoteRev,
        sourceQuoteRef: base.sourceQuoteRef,
        sourceIssuer: base.sourceIssuer,
        quoteJobTitle: joined('quoteJobTitle'),
        quoteJobQuoteRequestNo: joined('quoteJobQuoteRequestNo'),
        quoteJobStrataPlan: joined('quoteJobStrataPlan'),
        // A quote's split area and imported Rev never change once set, so the
        // cached row's own value fills in for a snapshot written before them.
        splitArea: base.splitArea ?? own.splitArea,
        importedRev: base.importedRev ?? own.importedRev,
        siteVisitRequestedAt: base.siteVisitRequestedAt,
    };
    if (!snapshotIsCurrent) return { jobFields, lifecycle: {} };

    // Send-state groups this device wrote after the snapshot keep the cached value.
    const receipts = row?.[LIFECYCLE_RECEIPTS_AT_FIELD];
    const keepCached = new Set<LifecycleReceiptGroup>();
    if (receipts && typeof receipts === 'object') {
        for (const group of Object.keys(LIFECYCLE_RECEIPT_GROUPS) as LifecycleReceiptGroup[]) {
            const at = timeOf(receipts[group]);
            if (Number.isFinite(at) && (!Number.isFinite(snapAt) || at > snapAt)) keepCached.add(group);
        }
    }
    // An email this device sent after the snapshot clears that Rev's site
    // visit request, as the server does once the Rev is sent.
    if (keepCached.has('email') && readSummaryLifecycleFields(row).emailSent) jobFields.siteVisitRequestedAt = null;
    return { jobFields, lifecycle: lifecycleOverride(entry.lifecycle, keepCached) };
}
